import Guid from "@/app/types/common/guid";
import { MenuItem } from "@/app/ui/navigation/types";
import type { Route } from "vue-router";
import tenantStore from "@/store/modules/tenantStore";
import { EnumAccountsPackageType } from "@/app/services/generated/enums/enumAccountsPackageType.generated";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";
import { hasFeatureUsingUserStore } from "@/app/featureFlags";
import { EnumFeatureFlag } from "@/app/services/generated/enums/enumFeatureFlag.generated";
import { RouteBuilder } from "@/app/ui/navigation/routeBuilder";
import { keys } from "@/plugins/i18n";
import userStore from "@/store/modules/userStore";

const builder = new RouteBuilder("Admin_");

const extractIdEditingPropsFromIdModeRoute = (route: Route) => ({
  id: route.params.id,
  editing: route.params.id && route.params.mode.toLowerCase().trim() === "edit",
});

const formIdAndModeLocationParams = (opts: { id: Guid; editing: boolean }) => ({
  id: opts.id.value,
  mode: opts.editing ? "edit" : "view",
});

export const documentSubRoutes = {
  designTab: builder.subRouteWithSingleIdParam({
    path: "/administration/documents/:id/design",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/documents/DesignTab.vue"
      ),
  }),
  contentTab: builder.subRouteWithSingleIdParam({
    path: "/administration/documents/:id/content",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/documents/ContentTab.vue"
      ),
  }),
  emailTab: builder.subRouteWithSingleIdParam({
    path: "/administration/documents/:id/email",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/documents/EmailTab.vue"
      ),
  }),
  settingsTab: builder.subRouteWithSingleIdParam({
    path: "/administration/documents/:id/settings",
    component: () =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/documents/SettingsTab.vue"
      ),
  }),
};

export const adminRoutes = {
  taskQueues: builder.route({
    url: "/administration/settings/production/taskqueues",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListTaskQueues.vue"
      ),
  }),
  taskTypes: builder.route({
    url: "/administration/settings/production/tasktypes",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListTaskTypes.vue"
      ),
  }),
  leadsLossReasons: builder.route({
    url: "/administration/settings/tasks/leadlossreaons",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListLeadLossReasons.vue"
      ),
  }),
  leadsSources: builder.route({
    url: "/administration/settings/tasks/leadsources",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListLeadSources.vue"
      ),
  }),
  leadsSalesPipelineStages: builder.route({
    url: "/administration/settings/tasks/leadspipelinestages",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListLeadPipelineStages.vue"
      ),
  }),

  creditTerms: builder.route({
    url: "/administration/settings/contacts/creditterms",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListCreditTerms.vue"
      ),
  }),
  communicationSubjects: builder.route({
    url: "/administration/settings/production/communicationsubjects",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListCommunicationSubjects.vue"
      ),
  }),
  communicationTypes: builder.route({
    url: "/administration/settings/production/communicationtypes",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListCommunicationTypes.vue"
      ),
  }),
  outletRatings: builder.route({
    url: "/administration/settings/contacts/outletratings",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListOutletRatings.vue"
      ),
  }),
  outletTypes: builder.route({
    url: "/administration/settings/contacts/outlettypes",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListOutletTypes.vue"
      ),
  }),
  outletSubTypes: builder.route({
    url: "/administration/settings/contacts/list-outlet-sub-types",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListOutletSubTypesPage.vue"
      ),
  }),
  salesAreas: builder.route({
    url: "/administration/settings/contacts/salesareas",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListSalesAreas.vue"
      ),
  }),
  salesCodes: builder.route({
    url: "/administration/settings/contacts/salescodes",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListSalesCodes.vue"
      ),
  }),

  glCodes: builder.route({
    url: "/administration/settings/contacts/glcodes",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListGlCodes.vue"
      ),
  }),
  packageTypes: builder.route({
    url: "/administration/settings/contacts/packagetypes",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListPackageTypes.vue"
      ),
  }),
  listPoDeliveryServices: builder.route({
    url: "/administration/settings/contacts/podeliveryservices",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListPoDeliveryService.vue"
      ),
  }),
  editPoDeliveryService: builder.routeWithParam(
    {
      url: "/administration/settings/contacts/podeliveryservices/:id/:mode",
      view: () =>
        import(
          /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/EditPoDeliveryService.vue"
        ),
      componentProps: extractIdEditingPropsFromIdModeRoute,
    },
    formIdAndModeLocationParams
  ),
  createPoDeliveryService: builder.route({
    url: "/administration/settings/contacts/podeliveryservices/create",
    view: () =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/EditPoDeliveryService.vue"
      ),
    componentProps: () => ({
      editing: true,
    }),
  }),
  pricingCategories: builder.route({
    url: "/administration/settings/contacts/pricingcategories",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListPricingCategories.vue"
      ),
  }),
  stockLocations: builder.route({
    url: "/administration/settings/contacts/stocklocations",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListStockLocations.vue"
      ),
  }),
  stockGroups: builder.route({
    url: "/administration/settings/contacts/stockgroups",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListStockGroups.vue"
      ),
  }),
  unitsOfMeasure: builder.route({
    url: "/administration/settings/contacts/unitsofmeasure",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListUnitsOfMeasure.vue"
      ),
  }),
  vatCodes: builder.route({
    url: "/administration/settings/contacts/vatcodes",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListVatCodes.vue"
      ),
  }),
  listEprBoms: builder.route({
    url: "/administration/settings/eprbom/list",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/pages/stock/epr/eprboms/EprBomListPage.vue"
      ),
  }),
  editEprBom: builder.routeWithParam(
    {
      url: "/administration/settings/eprbom/:id/:mode",
      view: () =>
        import(
          /* webpackChunkName: "administration" */ "@/pages/stock/epr/eprboms/ViewEprBomPage.vue"
        ),
      componentProps: (route) => ({
        id: Guid.tryParse(route.params.id),
        mode: route.params.mode,
      }),
    },
    formIdAndModeLocationParams
  ),
  createEprBpm: builder.route({
    url: "/administration/settings/eprbom/create",
    view: () =>
      import(
        /* webpackChunkName: "administration" */ "@/pages/stock/epr/eprboms/ViewEprBomPage.vue"
      ),
    componentProps: () => ({
      mode: "create",
    }),
  }),
  caskTypes: builder.route({
    url: "/administration/settings/agingcasks/casktype",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListCaskTypes.vue"
      ),
  }),
  caskSubTypes: builder.route({
    url: "/administration/settings/agingcasks/casksubtypes",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListCaskSubTypes.vue"
      ),
  }),
  caskSubLocations: builder.route({
    url: "/administration/settings/agingcasks/casksublocations",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListCaskSubLocations.vue"
      ),
  }),
  caskSubSubLocations: builder.route({
    url: "/administration/settings/agingcasks/casksubsublocations",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListCaskSubSubLocations.vue"
      ),
  }),
  vesselTypes: builder.route({
    url: "/administration/settings/production/vesseltypes",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListVesselTypes.vue"
      ),
  }),
  vesselStates: builder.route({
    url: "/administration/settings/production/vesselstates",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListVesselStates.vue"
      ),
  }),
  vessels: builder.route({
    url: "/administration/settings/production/vessels",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListVessels.vue"
      ),
  }),
  vesselSummarySettings: builder.route({
    url: "/administration/settings/production/vessel-summary-settings",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/VesselSummarySettings.vue"
      ),
  }),
  maintenanceProcesses: builder.route({
    url: "/administration/settings/production/maintenanceprocesses",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListMaintenanceProcesses.vue"
      ),
  }),
  qualityControlTests: builder.route({
    url: "/administration/settings/production/qctests",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListQcTests.vue"
      ),
  }),
  vesselCapabilities: builder.route({
    url: "/administration/settings/production/vesselcapabilities",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListVesselCapabilities.vue"
      ),
  }),
  listBottleDepositRedemptionItems: builder.route({
    url: "/administration/settings/bottledeposit/redemptionitem",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListRedemptionItem.vue"
      ),
  }),
  listBottleDepositChargeItems: builder.route({
    url: "/administration/settings/bottledeposit/chargeitems",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListChargeItems.vue"
      ),
  }),
  editBottleDepositRedemptionItem: builder.routeWithParam(
    {
      url: "/administration/settings/bottledeposit/redemptionitem/:id/:mode",
      view: () =>
        import(
          /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/EditRedemptionItem.vue"
        ),
      componentProps: extractIdEditingPropsFromIdModeRoute,
    },
    formIdAndModeLocationParams
  ),
  createBottleDepositRedemptionItem: builder.route({
    url: "/administration/settings/bottledeposit/redemptionitem/create",
    view: () =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/EditRedemptionItem.vue"
      ),
    componentProps: () => ({
      editing: true,
    }),
  }),
  editBottleDepositChargeItem: builder.routeWithParam(
    {
      url: "/administration/settings/bottledeposit/chargeitems/:id/:mode",
      view: () =>
        import(
          /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/EditChargeItem.vue"
        ),
      componentProps: extractIdEditingPropsFromIdModeRoute,
    },
    formIdAndModeLocationParams
  ),
  createBottleDepositChargeItem: builder.route({
    url: "/administration/settings/bottledeposit/chargeitems/create",
    view: () =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/EditChargeItem.vue"
      ),
    componentProps: () => ({
      editing: true,
    }),
  }),

  customNumberingSchemas: builder.route({
    url: "/administration/settings/production/numberschemas",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListNumberSchemes.vue"
      ),
  }),
  orderTypeText: builder.route({
    url: "/administration/settings/other/ordertypetext",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListOrderTypeText.vue"
      ),
  }),
  listPalletTypes: builder.route({
    url: "/administration/settings/other/pallettypes",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/ListPalletTypes.vue"
      ),
  }),
  editPalletType: builder.routeWithParam(
    {
      url: "/administration/settings/other/pallettypes/:id/:mode",
      view: () =>
        import(
          /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/EditPalletType.vue"
        ),
      componentProps: extractIdEditingPropsFromIdModeRoute,
    },
    formIdAndModeLocationParams
  ),
  createPalletType: builder.route({
    url: "/administration/settings/other/pallettypes/create",
    view: () =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/code_files/EditPalletType.vue"
      ),
    componentProps: () => ({
      editing: true,
    }),
  }),
  listRegionalTaxes: builder.route({
    url: "/administration/settings/other/regionaltaxes",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/pages/admin/settings/other/RegionalTaxListPage.vue"
      ),
  }),

  accountsPost: builder.routeWithParam(
    {
      url: "/administration/accountslink/post",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "administration" */ "@/components/pages/administration/accounts_posting/PostTransactions.vue"
        ),
      componentProps: (r) => ({
        filterToOrderIds:
          r.params.filterToOrderIds
            ?.split("|")
            .map((guidString) => Guid.tryParse(guidString))
            .filter((guid) => guid) ?? undefined,
      }),
    },
    (opts: { orderIds?: readonly Guid[] }) => ({
      filterToOrderIds: opts.orderIds?.join("|"),
    })
  ),
  accountsRepost: builder.route({
    url: "/administration/accountslink/repost",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/accounts_posting/Repost.vue"
      ),
  }),

  importList: builder.route({
    url: "/administration/import",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/import/ImportOverview.vue"
      ),
  }),
  importView: builder.routeWithParam(
    {
      url: "/administration/import/wizard/:id?",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "administration" */ "@/components/pages/administration/import/ImportWizard.vue"
        ),
      componentProps: (r) => ({
        id: r.params.id,
        bulkUpdateAtStart: r.params.bulkUpdateAtStart === "true",
      }),
    },
    (opts: { id?: Guid; bulkUpdateAtStart?: boolean }) => ({
      id: opts.id?.toString(),
      bulkUpdateAtStart: opts.bulkUpdateAtStart?.toString(),
    })
  ),
  export: builder.route({
    url: "/administration/export",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/export/Export.vue"
      ),
  }),
  emailSettings: builder.route({
    url: "/administration/emailsettings",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/email_settings/EmailSettings.vue"
      ),
  }),
  emailAudit: builder.route({
    url: "/administration/emailaudit",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/email_audit/EmailAudit.vue"
      ),
  }),
  smsAudit: builder.route({
    url: "/administration/smsaudit",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/SmsAudit.vue"
      ),
  }),
  taskNotifications: builder.route({
    url: "/administration/taskNotifications",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/TaskNotifications.vue"
      ),
  }),
  updateOpenOrders: builder.route({
    url: "/administration/updateopenorders",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/pages/admin/updateopentransactions/UpdateOpenOrdersPage.vue"
      ),
  }),
  updateOpenCredits: builder.route({
    url: "/administration/updateopencredits",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/pages/admin/updateopentransactions/UpdateOpenCreditsPage.vue"
      ),
  }),
  UpdateOpenPurchaseOrders: builder.route({
    url: "/administration/updateopenpurchaseorders",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/pages/admin/updateopentransactions/UpdateOpenPurchaseOrdersPage.vue"
      ),
  }),
  listDocumentTemplates: builder.route({
    url: "/administration/documents/list",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/documents/ListDocumentTemplates.vue"
      ),
  }),
  listDocumentTags: builder.route({
    url: "/administration/documents/tags",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/documents/ListDocumentTags.vue"
      ),
  }),
  viewDocumentTemplate: builder.routeToHoldSubRoutes({
    url: "/administration/documents/:id",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/documents/ViewDocument.vue"
      ),
    componentProps: (route) => ({
      id: route.params.id,
    }),
    routeKey: (route) => "/administration/documents/" + route.params.id,
    childRoutes: [
      documentSubRoutes.designTab,
      documentSubRoutes.contentTab,
      documentSubRoutes.emailTab,
      documentSubRoutes.settingsTab,
    ],
  }),
  listEmailDispatchTemplates: builder.route({
    url: "/administration/dispatch_notifications/templates/email",
    view: () =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/dispatchNotifications/ListNotificationTemplates.vue"
      ),
  }),
  editEmailDispatchTemplate: builder.routeWithParam(
    {
      url: "/administration/dispatch_notifications/templates/email/:id/:mode",
      view: () =>
        import(
          /* webpackChunkName: "administration" */ "@/components/pages/administration/dispatchNotifications/ViewEmailNotificationTemplate.vue"
        ),
      componentProps: (route) => {
        return {
          id: route.params.id,
          mode: "view",
        };
      },
    },
    (params: { id: Guid }) => ({
      id: params.id.toString(),
    })
  ),

  createEmailDispatchTemplate: builder.route({
    url: "/administration/dispatch_notifications/templates/email/create",
    view: () =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/dispatchNotifications/ViewEmailNotificationTemplate.vue"
      ),
    componentProps: () => ({
      mode: "create",
    }),
  }),
  listSmsDispatchTemplates: builder.route({
    url: "/administration/dispatch_notifications/templates/sms",
    view: () =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/dispatchNotifications/ListSmsDispatchNotificationTemplates.vue"
      ),
  }),
  editSmsDispatchTemplate: builder.routeWithParam(
    {
      url: "/administration/dispatch_notifications/templates/sms/:id/:mode",
      view: () =>
        import(
          /* webpackChunkName: "administration" */ "@/components/pages/administration/dispatchNotifications/EditDispatchNotificationTemplateSms.vue"
        ),
      componentProps: (route) => {
        return {
          id: route.params.id,
          editing:
            route.params.id &&
            route.params.mode.toLowerCase().trim() === "edit",
        };
      },
    },
    (params: { id: Guid; isEditing: boolean }) => ({
      id: params.id.toString(),
      mode: params.isEditing ? "edit" : "view",
    })
  ),
  createSmsDispatchTemplate: builder.route({
    url: "/administration/dispatch_notifications/templates/sms/create",
    view: () =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/dispatchNotifications/EditDispatchNotificationTemplateSms.vue"
      ),
    componentProps: (route) => ({
      editing: true,
    }),
  }),
  listCurrentSessions: builder.route({
    url: "/administration/currentsessions",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/CurrentSessions.vue"
      ),
  }),
  systemSetup: builder.route({
    url: "/administration/systemsetup",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/SystemSetup.vue"
      ),
  }),
  companySetupEdit: builder.route({
    url: "/administration/companysetup/edit",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/CompanySetup.vue"
      ),
    componentProps: () => ({
      editing: true,
    }),
  }),
  companySetupView: builder.route({
    url: "/administration/companysetup/view",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/CompanySetup.vue"
      ),
    componentProps: () => ({
      editing: false,
    }),
  }),
  usersList: builder.route({
    url: "/administration/users",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/Users.vue"
      ),
  }),
  apiHelp: builder.route({
    url: "/administration/api-help",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/pages/help/ApiHelpPage.vue"
      ),
  }),
  userCreate: builder.route({
    url: "/administration/users/create",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/users/UserAdmin.vue"
      ),
  }),
  userView: builder.routeWithSingleIdParam({
    url: "/administration/users/:id",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/users/UserAdmin.vue"
      ),
  }),
  billingDetails: builder.route({
    url: "/administration/billing",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/billing/BillingDetails.vue"
      ),
  }),
  labelOrders: builder.route({
    url: "/administration/labelOrders",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/label_ordering/ConsumablesOrderForm.vue"
      ),
    componentProps: () => ({
      tenantGroupId: userStore.state.tenantGroup?.id,
      userId: userStore.state.user?.id,
      tenantId: userStore.state.tenant?.id,
    }),
  }),
  labelOrdersList: builder.route({
    url: "/administration/labelOrdersList",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/label_ordering/ListLabelOrders.vue"
      ),
  }),
  userRolesList: builder.route({
    url: "/administration/user/roles",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/user_roles/ListUserRoles.vue"
      ),
  }),
  userRoleEdit: builder.routeWithParam(
    {
      url: "/administration/user/role/:id?/:mode?",
      view: () =>
        import(
          /* webpackChunkName: "administration" */ "@/components/pages/administration/user_roles/EditUserRole.vue"
        ),
    },
    (params: { id?: Guid; mode: "edit" | "view" | "create" }) => ({
      id: params.id?.toString(),
      mode: params.mode === "create" ? undefined : params.mode,
    })
  ),
  resetDemoData: builder.route({
    url: "/administration/resetdemodata",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "administration" */ "@/components/pages/administration/ResetDemoData.vue"
      ),
  }),
};

export const buildAdministrationMenuItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: keys.navigation_administration_group.value,
    icon: "Settings",
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuAdministrationView]),
    subMenus: [
      MenuItem.forBranch({
        name: keys.administration_settings.value,
        subMenus: [
          MenuItem.forBranch({
            name: keys.administration_tasks_and_leads.value,
            subMenus: [
              MenuItem.forRoute(keys.task_queues, adminRoutes.taskQueues),
              MenuItem.forRoute(keys.task_types, adminRoutes.taskTypes),
              MenuItem.forRoute(
                keys.leads_lead_loss_reasons,
                adminRoutes.leadsLossReasons
              ),
              MenuItem.forRoute(keys.leads_sources, adminRoutes.leadsSources),
              MenuItem.forRoute(
                keys.leads_pipeline_stages,
                adminRoutes.leadsSalesPipelineStages
              ),
            ],
          }),
          MenuItem.forBranch({
            name: keys.administration_contacts.value,
            subMenus: [
              MenuItem.forRoute(keys.credit_terms, adminRoutes.creditTerms),
              MenuItem.forRoute(
                keys.communication_subjects,
                adminRoutes.communicationSubjects
              ),
              MenuItem.forRoute(
                keys.communication_types,
                adminRoutes.communicationTypes
              ),
              MenuItem.forRoute(keys.outlet_ratings, adminRoutes.outletRatings),
              MenuItem.forRoute(keys.outlet_types, adminRoutes.outletTypes),
              MenuItem.forRoute(
                keys.list_outlet_sub_types,
                adminRoutes.outletSubTypes
              ),
              MenuItem.forRoute(keys.sales_areas, adminRoutes.salesAreas),
              MenuItem.forRoute(keys.sales_codes, adminRoutes.salesCodes),
            ],
          }),
          MenuItem.forBranch({
            name: keys.products.value,
            subMenus: [
              MenuItem.forRoute(keys.gl_codes, adminRoutes.glCodes),
              MenuItem.forRoute(keys.package_types, adminRoutes.packageTypes),
              MenuItem.forRoute(
                keys.po_delivery_service,
                adminRoutes.listPoDeliveryServices
              ),
              MenuItem.forRoute(
                keys.pricing_categories,
                adminRoutes.pricingCategories
              ),
              MenuItem.forRoute(
                keys.stock_locations,
                adminRoutes.stockLocations
              ),
              MenuItem.forRoute(keys.stock_groups, adminRoutes.stockGroups),
              MenuItem.forRoute(
                keys.units_of_measure,
                adminRoutes.unitsOfMeasure
              ),
              MenuItem.forRoute(keys.vat_codes, adminRoutes.vatCodes),
              MenuItem.forRoute(keys.stock_epr_boms, adminRoutes.listEprBoms, {
                visible: () =>
                  !tenantStore.state.tenantSettings.eprProducerStatus.isNone(),
              }),
            ],
          }),
          MenuItem.forBranch({
            name: keys.aging_casks.value,
            subMenus: [
              MenuItem.forRoute(
                keys.code_files_cask_type,
                adminRoutes.caskTypes
              ),
              MenuItem.forRoute(
                keys.code_files_cask_sub_type,
                adminRoutes.caskSubTypes
              ),
              MenuItem.forRoute(
                keys.code_files_cask_sub_location1,
                adminRoutes.caskSubLocations
              ),
              MenuItem.forRoute(
                keys.code_files_cask_sub_location2,
                adminRoutes.caskSubSubLocations
              ),
            ],
          }),
          MenuItem.forBranch({
            name: keys.production.value,
            subMenus: [
              MenuItem.forRoute(keys.vessel_types, adminRoutes.vesselTypes),
              MenuItem.forRoute(keys.vessel_vessels, adminRoutes.vessels),
              MenuItem.forRoute(keys.vessel_states, adminRoutes.vesselStates),
              MenuItem.forRoute(
                keys.vessel_summary_settings,
                adminRoutes.vesselSummarySettings
              ),
              MenuItem.forRoute(
                keys.vessels_maintenance_processes,
                adminRoutes.maintenanceProcesses,
                {
                  visible: () =>
                    hasFeatureUsingUserStore(EnumFeatureFlag.onlyDev) ||
                    !tenantStore.state.tenantSettings.isMegaBatchesEnabled,
                }
              ),
              MenuItem.forRoute(
                keys.qctests_title,
                adminRoutes.qualityControlTests
              ),
              MenuItem.forRoute(
                keys.vessel_capabilities,
                adminRoutes.vesselCapabilities
              ),
            ],
          }),
          MenuItem.forBranch({
            name: keys.bottle_deposit.value,
            visible: () =>
              tenantStore.state.bottleDepositSettings.isBottleDepositEnabled,
            subMenus: [
              MenuItem.forRoute(
                keys.bottle_deposit_redemption_item,
                adminRoutes.listBottleDepositRedemptionItems
              ),
              MenuItem.forRoute(
                keys.bottle_deposit_charge_items,
                adminRoutes.listBottleDepositChargeItems
              ),
            ],
          }),
          MenuItem.forBranch({
            name: keys.administration_other.value,
            subMenus: [
              MenuItem.forRoute(
                keys.number_schemes_title,
                adminRoutes.customNumberingSchemas
              ),
              MenuItem.forRoute(
                keys.orders_order_type_text,
                adminRoutes.orderTypeText
              ),
              MenuItem.forRoute(keys.pallet_types, adminRoutes.listPalletTypes),
              MenuItem.forRoute(
                keys.enum_noun_regional_tax_plural,
                adminRoutes.listRegionalTaxes,
                {
                  visible: () =>
                    tenantStore.state.tenantSettings.usesRegionalTax,
                }
              ),
            ],
          }),
        ],
      }),
      MenuItem.forBranch({
        name: keys.accounts_posting.value,
        subMenus: [
          MenuItem.forRoute(
            keys.accounts_posting_transactions,
            adminRoutes.accountsPost
          ),
          MenuItem.forRoute(keys.accounts_repost, adminRoutes.accountsRepost, {
            visible: () => {
              // We have to use .key here as the userStore injects additional data into the state object properties, meaning
              // that checking for equality doesn't work as expected
              return (
                tenantStore.state.accountsLinkDetails.accountsPackageType
                  .key !== EnumAccountsPackageType.lexoffice.key
              );
            },
          }),
        ],
      }),
      MenuItem.forBranch({
        name: keys.menu_data_management.value,
        subMenus: [
          MenuItem.forRoute(keys.import_external, adminRoutes.importList),
          MenuItem.forRoute(keys.export_external, adminRoutes.export),
          MenuItem.forRoute(
            keys.update_open_orders,
            adminRoutes.updateOpenOrders
          ),
          MenuItem.forRoute(
            keys.update_open_credits,
            adminRoutes.updateOpenCredits
          ),
          MenuItem.forRoute(
            keys.update_open_purchase_orders,
            adminRoutes.UpdateOpenPurchaseOrders
          ),
        ],
      }),
      MenuItem.forBranch({
        name: keys.menu_communication_and_notifications.value,
        subMenus: [
          MenuItem.forRoute(
            keys.document_template_list,
            adminRoutes.listDocumentTemplates
          ),
          MenuItem.forRoute(
            keys.dispatch_notification_email_template_list,
            adminRoutes.listEmailDispatchTemplates
          ),
          MenuItem.forRoute(
            keys.dispatch_notification_sms_template_list,
            adminRoutes.listSmsDispatchTemplates
          ),
          MenuItem.forRoute(
            keys.administration_email_settings,
            adminRoutes.emailSettings
          ),
          MenuItem.forRoute(keys.email_audit, adminRoutes.emailAudit),
          MenuItem.forRoute(keys.sms_audit, adminRoutes.smsAudit),
          MenuItem.forRoute(
            keys.task_notifications,
            adminRoutes.taskNotifications
          ),
        ],
      }),

      MenuItem.forBranch({
        name: keys.menu_your_company_and_system.value,
        subMenus: [
          MenuItem.forRoute(
            keys.auth_current_sessions,
            adminRoutes.listCurrentSessions
          ),
          MenuItem.forRoute(keys.system_setup, adminRoutes.systemSetup),
          MenuItem.forRoute(keys.company_setup, adminRoutes.companySetupView),
          MenuItem.forRoute(keys.user_setup, adminRoutes.usersList),
          MenuItem.forRoute(keys.user_roles, adminRoutes.userRolesList),
          MenuItem.forRoute(keys.reset_demo_data, adminRoutes.resetDemoData),
        ],
      }),
    ],
    requiredUserPermissions: [EnumUserPermission.administration],
  }),
];
